/**
 * Activity Feed - Recent events table
 * @module components/starnexus/dashboard/ActivityFeed
 */

import React, { useEffect, useState, useCallback } from 'react';
import Table from '@amzn/awsui-components-console/table';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import Badge from '@amzn/awsui-components-console/badge';
import StatusIndicator from '@amzn/awsui-components-console/status-indicator';
import Link from '@amzn/awsui-components-console/link';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Button from '@amzn/awsui-components-console/button';

import { processingService } from '../../../services/starnexus';
import type { ProcessingEvent } from '../../../types/starnexus';

const formatTimestamp = (date: Date): string => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return date.toLocaleDateString();
};

const getEventTypeBadge = (eventType: string): React.ReactNode => {
  const typeMap: Record<string, { color: 'blue' | 'green' | 'red' | 'grey'; label: string }> = {
    file_uploaded: { color: 'blue', label: 'Upload' },
    file_processed: { color: 'green', label: 'Processed' },
    file_failed: { color: 'red', label: 'Failed' },
    processing_started: { color: 'blue', label: 'Processing' },
    config_updated: { color: 'grey', label: 'Config' },
    dataset_enabled: { color: 'green', label: 'Enabled' },
    dataset_disabled: { color: 'grey', label: 'Disabled' },
    // Andes access events
    permission_requested: { color: 'blue', label: 'Access Req' },
    permission_approved: { color: 'green', label: 'Approved' },
    permission_rejected: { color: 'red', label: 'Rejected' },
    subscription_created: { color: 'blue', label: 'Sub Created' },
    subscription_activated: { color: 'green', label: 'Sub Active' },
    subscription_failed: { color: 'red', label: 'Sub Failed' },
    subscription_deleted: { color: 'grey', label: 'Sub Deleted' }
  };

  const config = typeMap[eventType] || { color: 'grey', label: eventType };
  return <Badge color={config.color}>{config.label}</Badge>;
};

const getStatusIndicator = (status: string): React.ReactNode => {
  const statusMap: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
    success: 'success',
    warning: 'warning',
    error: 'error',
    info: 'info'
  };

  return <StatusIndicator type={statusMap[status] || 'info'}>{status.charAt(0).toUpperCase() + status.slice(1)}</StatusIndicator>;
};

export const ActivityFeed: React.FC = () => {
  const [events, setEvents] = useState<ProcessingEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    try {
      const events = await processingService.getEvents(undefined, 10);
      setEvents(events);
    } catch {
      // Keep existing state on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();

    // Auto-refresh every 5 seconds
    const interval = setInterval(fetchEvents, 5000);
    return () => clearInterval(interval);
  }, [fetchEvents]);

  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            <Button iconName="refresh" onClick={fetchEvents} loading={loading}>
              Refresh
            </Button>
          }
        >
          Recent Activity
        </Header>
      }
    >
      <Table
        items={events}
        loading={loading}
        loadingText="Loading activity..."
        columnDefinitions={[
          {
            id: 'timestamp',
            header: 'Time',
            cell: (item) => formatTimestamp(item.timestamp),
            width: 100
          },
          {
            id: 'eventType',
            header: 'Event',
            cell: (item) => getEventTypeBadge(item.eventType),
            width: 140
          },
          {
            id: 'dataset',
            header: 'Dataset',
            cell: (item) => <Link href={`#/datasets/${item.datasetId}`}>{item.datasetName}</Link>
          },
          {
            id: 'status',
            header: 'Status',
            cell: (item) => getStatusIndicator(item.status),
            width: 120
          },
          {
            id: 'user',
            header: 'User',
            cell: (item) => item.userName,
            width: 130
          }
        ]}
        empty={
          <Box textAlign="center" color="inherit">
            <SpaceBetween size="m">
              <b>No recent activity</b>
              <Box variant="p" color="inherit">
                Activity will appear here as files are processed.
              </Box>
            </SpaceBetween>
          </Box>
        }
        variant="embedded"
      />
    </Container>
  );
};

export default ActivityFeed;
